import { Plain } from '@/app/technical';
import type { ReactNode } from 'react';

/**
 * Where a thing stands (FRONTEND-AUDIT §E): how far along a flow is, a
 * state the whole page is in, a mechanism built and not yet firing, and
 * what started an action. Each was drawn ad hoc, screen by screen.
 */

/** How far along a flow of several steps is; the gallery's bar, lifted out of Dialog. */
export function Stepper({ at, of, labelled = false }: { at: number; of: number; labelled?: boolean }) {
  if (of <= 1) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-1" role="img" aria-label={`Step ${at + 1} of ${of}`}>
        {Array.from({ length: of }, (_, i) => (
          <span key={i} className={`h-[3px] flex-1 ${i <= at ? 'bg-accent' : 'bg-line'}`} />
        ))}
      </div>
      {labelled && (
        <span className="text-micro uppercase tracking-label text-meta">
          Step {at + 1} of {of}
        </span>
      )}
    </div>
  );
}

/**
 * A state the page is in, said once at its top: the stop is engaged, a
 * department only reads, something waits on a person. A rule on the left in
 * its tone, a title, what it means, and the one thing to do.
 */
export function Banner({
  tone = 'neutral',
  title,
  children,
  action,
}: {
  tone?: 'neutral' | 'accent' | 'warning' | 'danger';
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  const edge = { neutral: 'border-l-strong', accent: 'border-l-accent', warning: 'border-l-warning', danger: 'border-l-danger' }[tone];
  return (
    <section
      role={tone === 'danger' ? 'alert' : 'status'}
      className={`flex flex-wrap items-center gap-x-6 gap-y-3 border border-line border-l-[3px] ${edge} bg-raised px-5 py-4`}
    >
      <div className="flex min-w-[220px] flex-1 flex-col gap-1">
        <span className={`text-dense font-semibold ${tone === 'danger' ? 'text-danger' : 'text-ink'}`}><Plain>{title}</Plain></span>
        {children && <div className="text-dense text-secondary"><Plain>{children}</Plain></div>}
      </div>
      {action}
    </section>
  );
}

/**
 * Built, and not firing yet — a dashed gold mark with the reason. For a
 * mechanism the platform has and the deployment has not switched on, so a
 * screen never shows a zero that means "nothing measured".
 */
export function NotYetLive({ label, why }: { label: string; why: string }) {
  return (
    <span className="inline-flex max-w-full flex-col gap-0.5 border border-dashed border-warning px-3 py-2" data-testid="not-yet-live">
      <span className="text-micro uppercase tracking-label text-ink">{label} · not live yet</span>
      <span className="text-dense text-secondary">{why}</span>
    </span>
  );
}

/** What starts a twin's or an agent's action (ADR-0058); an inbound message never does. */
export type Origin = 'chat' | 'confirmation' | 'assignment' | 'schedule';

const STARTED: Record<Origin, (person: string) => string> = {
  chat: (p) => `${p} asked in the chat`,
  confirmation: (p) => `${p} confirmed it`,
  assignment: () => 'an assignment a person made',
  schedule: () => 'the platform’s schedule, under a standing rule',
};

/** "Started by …", on every action an agent took (ADR-0058). */
export function OriginLine({ origin, person = 'you' }: { origin: Origin; person?: string }) {
  return (
    <span
      className="text-micro uppercase tracking-[0.08em] text-secondary"
      title="An agent acts on exactly four things: a request in chat, a person's confirmation, an assignment a person made, or the schedule under a standing rule. Never an inbound message."
      data-testid="origin-line"
    >
      Started by {STARTED[origin](person)}
    </span>
  );
}
